// Shared behavior for Vue 2 and the local Node harness; no browser or store dependency.
const channels = { Venta: 'VN', 'Plan de Ahorro': 'PDA', Servicio: 'PV' }
const recipientTypes = Object.values(channels).reduce((types, channel) =>
  types.concat(['SALTA', 'JUJUY'].map(branch => `RAC - ${channel} ${branch}`)), []).concat(
  ['SALTA', 'JUJUY'].map(branch => `RAC - PV LAVADERO - ${branch}`))

function numericScore(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const text = String(value).trim()
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null
  const score = Number(text)
  return Number.isInteger(score) && score >= 1 && score <= 10 ? score : null
}

const rowColorOptions = [
  { value: 'verde', text: 'Verde', color: 'green' },
  { value: 'amarillo', text: 'Amarillo', color: 'amber darken-2' },
  { value: 'rojo', text: 'Rojo', color: 'red' },
  { value: 'violeta', text: 'Violeta', color: 'deep-purple' }
]

// Manual color wins; without it, the row follows the score rule requested for
// the quality report: 9-10 green, 8 or less red, no color otherwise.
function effectiveRowColor(row) {
  const manual = rowColorOptions.find(option => option.value === row.colorFila)
  if (manual) return manual.value
  const score = numericScore(row.puntuacion)
  if (score === null) return null
  return score >= 9 ? 'verde' : 'rojo'
}

function calculateNps(rows) {
  const seen = new Set()
  const result = { promoters: 0, detractors: 0, passives: 0, respondents: 0, value: null }
  for (const row of rows) {
    if (!row.responseKey || seen.has(row.responseKey)) continue
    seen.add(row.responseKey)
    const score = numericScore(row.puntuacion)
    if (score === null) continue
    result.respondents++
    if (score >= 9) result.promoters++
    else if (score <= 6) result.detractors++
    else result.passives++
  }
  if (result.respondents) result.value = 100 * (result.promoters - result.detractors) / result.respondents
  return result
}

function responseDraft(row, commentOnly = false) {
  return { item: row, commentOnly, step: 1, puntuacion: numericScore(row.puntuacion),
    comentario: row.comentario || '', busy: false, error: '' }
}

function responsePayload(draft) {
  const payload = { canal: draft.item.canal, sourceId: draft.item.sourceId }
  if (draft.commentOnly || draft.comentario !== (draft.item.comentario || '')) payload.comentario = draft.comentario
  if (!draft.commentOnly) {
    if (!/^(?:[1-9]|10)$/.test(String(draft.puntuacion))) throw new Error('Seleccione una puntuación de 1 a 10')
    payload.puntuacion = draft.puntuacion
  }
  return payload
}

function syncResponse(rows, saved) {
  return rows.map(row => row.responseKey === saved.responseKey
    ? { ...row, puntuacion: saved.puntuacion, comentario: saved.comentario } : row)
}

function syncContact(rows, saved) {
  return rows.map(row => row.clientKey && String(row.clientKey) === String(saved.clientKey)
    ? { ...row, telefono: saved.telefono, email: saved.email, celular: saved.celular } : row)
}

function racManualEligible(row, resend = false) {
  return !!row.sourceId && !!row.responseKey && Object.prototype.hasOwnProperty.call(channels, row.canal) &&
    row.racEstado === (resend ? 'enviado' : 'pendiente')
}

function racEligible(row, resend = false) {
  return racManualEligible(row, resend) && ['SALTA', 'JUJUY'].includes(row.racSucursal)
}

function racDetractor(row) {
  const score = numericScore(row.puntuacion)
  return racEligible(row) && score !== null && score <= 6
}

function selectableRows(rows, allRows = rows) {
  const groups = new Map()
  allRows.forEach(row => {
    const key = row.responseKey
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(row)
  })
  const seen = new Set()
  const scoreKey = row => numericScore(row.puntuacion) ?? String(row.puntuacion ?? '').trim()
  return rows.map((row, index) => {
    const group = groups.get(row.responseKey)
    const isSelectable = racEligible(row) && !seen.has(row.responseKey) &&
      group.every(other => racEligible(other) && scoreKey(other) === scoreKey(row) && other.racSucursal === row.racSucursal)
    seen.add(row.responseKey)
    return { ...row, racRowKey: `${row.responseKey || 'unresolved'}:${index}`, isSelectable }
  })
}

function batchIdentities(rows, allRows = rows) {
  return selectableRows(rows, allRows).filter(row => row.isSelectable).map(row => ({ canal: row.canal, sourceId: row.sourceId }))
}

function syncRac(rows, results) {
  const states = new Map(results.map(result => [`${result.canal}:${result.sourceId}`, result]))
  return rows.map(row => {
    const result = states.get(row.responseKey)
    if (!result) return row
    return { ...row, racEstado: result.estado === 'omitido' ? 'enviado' : result.estado,
      racMessage: result.message || '', racFechaEnvio: result.racFechaEnvio || null }
  })
}

function racDraft(row, resend = false) {
  return { item: row, channel: channels[row.canal] || '', sucursal: ['SALTA', 'JUJUY'].includes(row.racSucursal) ? row.racSucursal : null, preview: null,
    destino: 'postventa', recipients: [], recipientIds: null,
    previewRevision: 0, busy: false, sending: false, error: '', sent: false, resend }
}

function racPayload(draft) {
  if (!draft.channel || !['SALTA', 'JUJUY'].includes(draft.sucursal)) throw new Error('Seleccione Salta o Jujuy')
  if (['SALTA', 'JUJUY'].includes(draft.item.racSucursal) && draft.sucursal !== draft.item.racSucursal) {
    throw new Error('La sucursal debe coincidir con el origen')
  }
  if (!['postventa', 'lavadero'].includes(draft.destino) || (draft.destino === 'lavadero' && draft.item.canal !== 'Servicio')) {
    throw new Error('Destino RAC inválido para el canal')
  }
  if (draft.recipientIds !== null && !draft.recipientIds.length) throw new Error('Seleccione al menos un destinatario')
  return { canal: draft.item.canal, sourceId: draft.item.sourceId, sucursal: draft.sucursal,
    ...(draft.destino === 'lavadero' ? { destino: draft.destino } : {}),
    ...(draft.recipientIds === null ? {} : { recipientIds: [...draft.recipientIds] }) }
}

module.exports = { channels, recipientTypes, numericScore, calculateNps, responseDraft, responsePayload,
  syncResponse, syncContact, racDraft, racPayload, racManualEligible, racEligible, racDetractor, selectableRows, batchIdentities, syncRac,
  rowColorOptions, effectiveRowColor }
